import { brand } from '@enp/brand';
import {
  BrandMark,
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  focusRing,
  IconButton,
  Input,
  OfflineBanner,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Skeleton,
  SyncIndicator,
  Tooltip,
  useThemePreference,
  type ThemePreference,
} from '@enp/ui';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  Keyboard,
  LogOut,
  Menu,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from 'lucide-react';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { meQueryOptions, signOut, type Me } from '../auth/session.js';
import { waitingApprovalsQuery, waitingText } from '../stock-control/queries.js';
import { useConnection } from './connection.js';
import { canOpenArea } from '@enp/domain/permissions';
import { NAV_GROUPS, navFor, sectionForPath } from './nav.js';
import { NoAccess } from './no-access.js';
import { useShortcuts } from './shortcuts.js';
import { focusPageSearch, useOfflineCardShown, usePageHasSearch } from './signals.js';

const wideQuery = '(min-width: 1280px)';
const initiallyCollapsed = () =>
  typeof window !== 'undefined' && window.matchMedia
    ? !window.matchMedia(wideQuery).matches
    : false;

/**
 * How many approval requests wait (UI4A-4), beside the Approvals item; nothing when none
 * wait or the member can't read them.
 */
function WaitingCount({ collapsed }: { collapsed: boolean }) {
  const { data: me } = useQuery(meQueryOptions());
  const allowed = me ? canOpenArea(me.permissions, 'approvals') : false;
  const { data } = useQuery({ ...waitingApprovalsQuery(), enabled: allowed });
  if (!data || data.count === 0) return null;
  return (
    <span
      data-testid="nav-waiting"
      className={cn(
        'rounded-full bg-primary px-1.5 text-xs font-semibold leading-5 tabular-nums text-primary-foreground',
        collapsed && 'absolute right-0.5 top-0.5 px-1 text-[0.625rem] leading-4',
      )}
    >
      <span className="sr-only">, </span>
      {waitingText(data)}
      <span className="sr-only"> waiting</span>
    </span>
  );
}

function SidebarNav({
  collapsed,
  onNavigate,
  permissions,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
  /** Only the sections these open are listed (D-070); all while unknown. */
  permissions: readonly string[] | undefined;
}) {
  const items = navFor(permissions);
  return (
    <nav aria-label="Main" className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
      {NAV_GROUPS.filter((group) => items.some((item) => item.group === group.id)).map((group) => (
        <div key={group.id} className="flex flex-col gap-0.5">
          <p
            className={cn(
              'px-2.5 pb-1.5 text-xs font-semibold uppercase tracking-wide text-muted',
              collapsed && 'sr-only',
            )}
          >
            {group.label}
          </p>
          {items
            .filter((item) => item.group === group.id)
            .map((item) => {
              const Icon = item.icon;
              const link = (
                <Link
                  key={item.to}
                  to={item.to}
                  activeOptions={{ exact: item.to === '/' }}
                  onClick={onNavigate}
                  className={cn(
                    'group flex h-9 items-center gap-3 rounded-md px-2.5 text-sm font-medium text-muted',
                    'transition-colors duration-(--duration-fast) hover:bg-surface-hover hover:text-fg',
                    'data-[status=active]:bg-primary-subtle data-[status=active]:text-primary-text',
                    collapsed && 'relative justify-center px-0',
                    focusRing,
                  )}
                >
                  <Icon aria-hidden className="size-4.5 shrink-0" />
                  <span className={cn('flex-1 truncate', collapsed && 'sr-only')}>
                    {item.label}
                  </span>
                  {item.to === '/approvals' && <WaitingCount collapsed={collapsed} />}
                  {!collapsed && (
                    <kbd
                      aria-hidden
                      className="hidden font-mono text-[0.6875rem] text-muted opacity-0 transition-opacity group-hover:opacity-100 xl:inline"
                    >
                      g {item.shortcut}
                    </kbd>
                  )}
                </Link>
              );
              return collapsed ? (
                <Tooltip key={item.to} content={item.label} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
        </div>
      ))}
    </nav>
  );
}

function ShortcutHelp({
  open,
  onOpenChange,
  permissions,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  permissions: readonly string[] | undefined;
}) {
  const rows: [string[], string][] = [
    ...navFor(permissions).map((item): [string[], string] => [
      ['g', item.shortcut],
      `Go to ${item.label}`,
    ]),
    [['/'], 'Search'],
    [['?'], 'Show this list'],
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press the keys one after the other, outside a text field.
          </DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
          {rows.map(([keys, label]) => (
            <div key={label} className="contents">
              <dt className="flex gap-1">
                {keys.map((k) => (
                  <kbd
                    key={k}
                    className="min-w-6 rounded-sm border border-border-strong bg-surface-sunken px-1.5 text-center font-mono text-xs leading-6"
                  >
                    {k}
                  </kbd>
                ))}
              </dt>
              <dd className="text-fg">{label}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}

/** Signs out and returns to the sign-in page. Works even when /me didn't load. */
function useSignOut() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await signOut(router.options.context.auth, queryClient);
    await navigate({ to: '/login', search: { reason: 'signed-out' } });
  };
}

function UserMenu({ me, onShowShortcuts }: { me: Me; onShowShortcuts: () => void }) {
  const doSignOut = useSignOut();
  const [theme, setTheme] = useThemePreference();
  const initials = me.user.email.slice(0, 2).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${me.user.email}`}
          className={cn(
            'flex h-9 items-center gap-2 rounded-full pl-1 pr-2 text-sm hover:bg-surface-hover',
            focusRing,
          )}
        >
          {/* Neutral: the avatar is neither an action colour nor a status. */}
          <span
            aria-hidden
            className="flex size-7 items-center justify-center rounded-full border border-border-strong bg-surface-sunken text-xs font-semibold text-fg"
          >
            {initials}
          </span>
          <ChevronDown aria-hidden className="size-4 text-muted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2 font-normal">
          <span className="truncate text-sm font-medium text-fg">{me.user.email}</span>
          <span className="text-xs text-muted" data-testid="account-role">
            {me.role.name} · {me.account.name}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as ThemePreference)}>
          <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">Match this device</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onShowShortcuts}>
          <Keyboard aria-hidden />
          Keyboard shortcuts
          <DropdownMenuShortcut>?</DropdownMenuShortcut>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/design">
            <Palette aria-hidden />
            Design system
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void doSignOut()}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Connection state in the top bar. The Admin has nothing to sync, so all-is-well is a quiet
 * "Connected"; the server not answering (device online) is an amber warning; offline is
 * the loudest (UI0A-201).
 */
function ConnectionIndicator() {
  const { status } = useConnection();
  const view = {
    connected: { status: 'online', label: 'Connected' },
    unreachable: { status: 'unreachable', label: `Can’t reach ${brand.productName}` },
    offline: { status: 'offline', label: 'Offline' },
  } as const;
  return (
    <SyncIndicator
      status={view[status].status}
      label={view[status].label}
      quiet={status === 'connected'}
      data-testid="connection"
      className="hidden whitespace-nowrap sm:inline-flex"
    />
  );
}

export interface ShellFrameProps {
  /** The signed-in user. Absent while /me loads or when it failed: the frame still works. */
  me?: Me;
  /**
   * `pending`: the session is still being checked, so the account area shows placeholders
   * (not a "Sign out" that would flash and vanish). `error`: /me failed, so "Sign out" is
   * the way out.
   */
  session?: 'ready' | 'pending' | 'error';
  children: ReactNode;
}

/** Placeholders for the account name and avatar while the session is checked. */
function AccountSkeleton() {
  return (
    <span className="flex items-center gap-3" data-testid="account-pending">
      <Skeleton className="hidden h-4 w-32 md:block" />
      <Skeleton className="size-7 rounded-full" />
      <span className="sr-only">Loading your account</span>
    </span>
  );
}

/**
 * The Admin frame: sidebar, top bar (search, connection, account) and `main`. Loading and
 * error states render inside it too, so navigation and sign-out never disappear.
 */
export function ShellFrame({ me, session = me ? 'ready' : 'error', children }: ShellFrameProps) {
  const navigate = useNavigate();
  const doSignOut = useSignOut();
  const { online, since } = useConnection();
  const offlineCardShown = useOfflineCardShown();
  const pageHasSearch = usePageHasSearch();
  const [collapsed, setCollapsed] = useState(initiallyCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useShortcuts({
    navigate: (to) => void navigate({ to }),
    // A page with its own search (the catalogue) takes "/"; otherwise the top bar's.
    focusSearch: () => focusPageSearch() || searchRef.current?.focus(),
    openHelp: () => setHelpOpen(true),
    permissions: me?.permissions,
  });

  // The top-bar search finds products: it opens the catalogue with the text as its search.
  const onSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get('q')?.toString().trim() ?? '';
    void navigate({ to: '/catalogue', search: q ? { q } : {} });
    e.currentTarget.reset();
    searchRef.current?.blur();
  };

  return (
    <div className="flex min-h-dvh bg-surface print:bg-transparent">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-surface-raised px-4 py-2 font-medium text-fg shadow-md focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to content
      </a>

      <aside
        className={cn(
          'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-border bg-surface-raised lg:flex print:hidden',
          'transition-[width] duration-(--duration-base) ease-standard',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <div
          className={cn(
            'flex h-14 items-center border-b border-border px-4',
            collapsed && 'justify-center px-0',
          )}
        >
          <Link
            to="/"
            aria-label={`${brand.productName} ${brand.appNames.admin}: dashboard`}
            className={cn('rounded-md', focusRing)}
          >
            <BrandMark size="sm" withName={!collapsed} suffix={brand.appNames.admin} />
          </Link>
        </div>
        <SidebarNav collapsed={collapsed} permissions={me?.permissions} />
        <div className={cn('border-t border-border p-3', collapsed && 'flex justify-center')}>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'w-full justify-start text-muted',
              collapsed && 'w-auto justify-center px-2',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => !c)}
            icon={collapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
          >
            {!collapsed && 'Collapse'}
          </Button>
        </div>
      </aside>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 gap-0 p-0">
          <div className="flex h-14 items-center border-b border-border px-4">
            <SheetTitle asChild>
              <span>
                <BrandMark size="sm" suffix={brand.appNames.admin} />
              </span>
            </SheetTitle>
            <SheetDescription className="sr-only">Main navigation</SheetDescription>
          </div>
          <SidebarNav
            collapsed={false}
            onNavigate={() => setDrawerOpen(false)}
            permissions={me?.permissions}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="sticky top-0 z-30 print:hidden">
          <header className="flex h-14 items-center gap-3 border-b border-border bg-surface/90 px-4 backdrop-blur lg:px-6">
            <IconButton
              className="lg:hidden"
              label="Open navigation"
              icon={<Menu aria-hidden />}
              onClick={() => setDrawerOpen(true)}
            />
            <form role="search" onSubmit={onSearch} className="relative w-full max-w-md">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
              />
              <Input
                ref={searchRef}
                type="search"
                name="q"
                aria-label="Search"
                aria-keyshortcuts={pageHasSearch ? undefined : '/'}
                placeholder="Search products by name, SKU or barcode…"
                className={cn('h-9 bg-surface-raised pl-8', !pageHasSearch && 'pr-9')}
              />
              {/* "/" belongs to the page's own search when it has one (UI1A-20). */}
              {!pageHasSearch && (
                <kbd
                  aria-hidden
                  className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-sm border border-border-strong px-1.5 font-mono text-xs text-muted"
                >
                  /
                </kbd>
              )}
            </form>
            <div className="ml-auto flex items-center gap-3">
              <ConnectionIndicator />
              {me && (
                // Plain text, not a control: one account per sign-in for now.
                <span className="hidden items-center gap-1.5 text-sm font-medium text-muted md:inline-flex">
                  <Building2 aria-hidden className="size-4" />
                  <span className="max-w-48 truncate text-fg" data-testid="account-name">
                    {me.account.name}
                  </span>
                </span>
              )}
              <IconButton
                label="Keyboard shortcuts"
                icon={<Keyboard aria-hidden />}
                onClick={() => setHelpOpen(true)}
                className="hidden md:inline-flex"
              />
              {me ? (
                <UserMenu me={me} onShowShortcuts={() => setHelpOpen(true)} />
              ) : session === 'pending' ? (
                <AccountSkeleton />
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<LogOut aria-hidden />}
                  onClick={() => void doSignOut()}
                >
                  Sign out
                </Button>
              )}
            </div>
          </header>
          {/* Once per screen: not when the page itself is the "You're offline" card. */}
          {!online && !offlineCardShown && (
            <OfflineBanner
              since={since}
              className="border-t-0"
              message="You can still read what’s on screen. Changes can’t be saved until you’re back online."
            />
          )}
        </div>

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 px-4 py-6 outline-none lg:px-8 lg:py-8 print:p-0"
        >
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>
      </div>

      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} permissions={me?.permissions} />
    </div>
  );
}

/**
 * The signed-in layout: the frame around the current page. A section the member can't open
 * (a typed or bookmarked address) shows the permission page, never the page's own error.
 */
export function AppShell() {
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const section = sectionForPath(pathname);
  const allowed = !section || canOpenArea(me.permissions, section.area);
  return (
    <ShellFrame me={me}>{allowed ? <Outlet /> : <NoAccess section={section.label} />}</ShellFrame>
  );
}
